import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Bot, Send, User, Loader2, MessageSquare, Plus, Clock, Zap, AlertTriangle, CheckCircle2, TrendingUp, Shield, Database } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HIZLI_SORULAR = [
  'Devam eden ihalelerimizin toplam sözleşme bedeli ne kadar?',
  'Hangi ihalelerde iş artışı oranı yasal sınıra yaklaşmış?',
  'Bitiş tarihi geçmiş ama tamamlanmamış ihaleler hangileri?',
  'Ödenek talepleri ile hakediş ödemelerini özetle',
  'Açık arıza kayıtları ve SLA aşımlarını listele',
  'Süre uzatımı hangi hallerde verilir? (YİGŞ Md.24)',
];

const MODUL_ETIKET = {
  ihale: 'İhale', isy_ihale: 'ISY İhale', odenek: 'Ödenek', hakedis: 'Hakediş', bakim_onarim: 'Bakım Onarım',
  dogrudan_temin: 'Doğrudan Temin', emlak: 'Emlak', pk_bina: 'PK Bina', pk_ekipman: 'PK Ekipman',
  pk_ariza: 'PK Arıza', pk_sozlesme: 'PK Sözleşme', gorev: 'Görev', santiye: 'Şantiye Kontrol',
};

const KaynakSatiri = ({ kaynaklar }) => {
  const eslesen = Object.entries(kaynaklar?.eslesen || {});
  if (!eslesen.length) return null;
  return (
    <div className="mt-1.5 pt-1.5 border-t border-gray-700/50 text-[10px] text-cyan-400/80 flex items-center gap-1 flex-wrap" data-testid="asistan-kaynaklar">
      <Database className="w-3 h-3" /> Kaynak:
      {eslesen.map(([k, v]) => <span key={k} className="bg-cyan-900/30 rounded px-1.5 py-0.5">{k} ({v})</span>)}
    </div>
  );
};

export default function ISYAsistan({ ihaleler }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [selectedIhale, setSelectedIhale] = useState('none');
  const [selectedProvider, setSelectedProvider] = useState('openai');
  const [showSessions, setShowSessions] = useState(false);
  const [proaktifLoading, setProaktifLoading] = useState(false);
  const [proaktifResult, setProaktifResult] = useState(null);
  const [ayarlar, setAyarlar] = useState(null);
  const [veriDurumu, setVeriDurumu] = useState(null);
  const [tumModuller, setTumModuller] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => { fetchSessions(); fetchAyarlar(); fetchVeriDurumu(); }, []);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const fetchVeriDurumu = async () => {
    try {
      const res = await axios.get(`${API}/isy/asistan/veri-durumu`, { withCredentials: true });
      setVeriDurumu(res.data);
    } catch (e) { /* ignore */ }
  };

  const fetchAyarlar = async () => {
    try {
      const res = await axios.get(`${API}/isy/ayarlar`, { withCredentials: true });
      setAyarlar(res.data);
      const hasKey = { openai: res.data.has_openai, anthropic: res.data.has_anthropic, gemini: res.data.has_gemini, custom: res.data.has_custom };
      if (res.data.default_provider && hasKey[res.data.default_provider]) setSelectedProvider(res.data.default_provider);
      else if (res.data.has_openai) setSelectedProvider('openai');
      else if (res.data.has_anthropic) setSelectedProvider('anthropic');
      else if (res.data.has_gemini) setSelectedProvider('gemini');
      else if (res.data.has_custom) setSelectedProvider('custom');
    } catch (e) { /* ignore */ }
  };

  const fetchSessions = async () => {
    try {
      const res = await axios.get(`${API}/isy/asistan/sessions`, { withCredentials: true });
      setSessions(res.data || []);
    } catch (e) { /* ignore */ }
  };

  const loadSession = async (sid) => {
    try {
      const res = await axios.get(`${API}/isy/asistan/history?session_id=${sid}`, { withCredentials: true });
      const msgs = (res.data || []).reverse().flatMap(m => [
        { role: 'user', content: m.user_message, time: m.created_at },
        { role: 'ai', content: m.ai_response, time: m.created_at, kaynaklar: m.kaynaklar }
      ]);
      setMessages(msgs);
      setSessionId(sid);
      setShowSessions(false);
    } catch (e) { toast.error('Sohbet yüklenemedi'); }
  };

  const newChat = () => { setMessages([]); setSessionId(null); };

  const sendMessage = async (text) => {
    if (!text?.trim()) return;
    const userMsg = { role: 'user', content: text, time: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);
    
    try {
      const payload = {
        message: text,
        session_id: sessionId,
        ihale_id: selectedIhale !== 'none' ? selectedIhale : null,
        provider: selectedProvider,
        tum_moduller: tumModuller
      };
      const res = await axios.post(`${API}/isy/asistan/chat`, payload, { withCredentials: true });
      const aiMsg = { role: 'ai', content: res.data.response, time: new Date().toISOString(), kaynaklar: res.data.kaynaklar };
      setMessages(prev => [...prev, aiMsg]);
      if (!sessionId) setSessionId(res.data.session_id);
      fetchSessions();
    } catch (e) {
      const errMsg = e.response?.data?.detail || 'AI yanıt hatası';
      toast.error(errMsg);
      setMessages(prev => [...prev, { role: 'ai', content: `Hata: ${errMsg}`, time: new Date().toISOString(), error: true }]);
    }
    finally { setLoading(false); }
  };

  const handleProaktifAnaliz = async () => {
    if (selectedIhale === 'none') { toast.error('Proaktif analiz için bir ihale seçin'); return; }
    setProaktifLoading(true);
    setProaktifResult(null);
    try {
      const res = await axios.post(`${API}/isy/asistan/proaktif-analiz/${selectedIhale}`, { provider: selectedProvider }, { withCredentials: true });
      setProaktifResult(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Proaktif analiz hatası');
    }
    finally { setProaktifLoading(false); }
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); } };

  return (
    <div className="flex flex-col h-[calc(100vh-120px)]" data-testid="isy-asistan">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Bot className="w-5 h-5 text-cyan-400" />
          <h2 className="text-sm font-bold text-white">Uzman Asistan</h2>
          <span className="flex items-center gap-1 text-[10px] text-emerald-300 bg-emerald-900/30 border border-emerald-800/40 rounded-full px-2 py-0.5" data-testid="asistan-veri-badge"
            title="Asistan her soruda platformdaki tüm ihaleleri ve ilgili modül kayıtlarını canlı veritabanından okur">
            <Database className="w-3 h-3" /> Platform verileri bağlı{veriDurumu ? ` · ${veriDurumu.toplam} kayıt` : ''}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-[11px] text-gray-400 cursor-pointer" title="Kapatılırsa yalnızca ihale envanteri + seçili ihale gönderilir; diğer modüller taranmaz">
            <Switch checked={tumModuller} onCheckedChange={setTumModuller} className="scale-75 data-[state=checked]:bg-cyan-600" data-testid="asistan-tum-moduller-toggle" />
            Tüm modülleri tara
          </label>
          {/* Model Seçimi — kullanıcının Profil › Yapay Zekâ'daki kendi anahtarları */}
          <Select value={selectedProvider} onValueChange={setSelectedProvider}>
            <SelectTrigger className="h-7 w-52 bg-gray-800 border-gray-700 text-white text-xs" data-testid="provider-select" title="Profil › Yapay Zekâ'da seçtiğiniz model sürümü kullanılır">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700">
              <SelectItem value="openai" className="text-gray-300 text-xs" disabled={!ayarlar?.has_openai}>
                {ayarlar?.has_openai ? `ChatGPT · ${ayarlar.models?.openai || ''}` : 'ChatGPT (anahtar yok)'}
              </SelectItem>
              <SelectItem value="anthropic" className="text-gray-300 text-xs" disabled={!ayarlar?.has_anthropic}>
                {ayarlar?.has_anthropic ? `Claude · ${ayarlar.models?.anthropic || ''}` : 'Claude (anahtar yok)'}
              </SelectItem>
              <SelectItem value="gemini" className="text-gray-300 text-xs" disabled={!ayarlar?.has_gemini}>
                {ayarlar?.has_gemini ? `Gemini · ${ayarlar.models?.gemini || ''}` : 'Gemini (anahtar yok)'}
              </SelectItem>
              {ayarlar?.has_custom && <SelectItem value="custom" className="text-gray-300 text-xs">Özel endpoint · {ayarlar.models?.custom || ''}</SelectItem>}
            </SelectContent>
          </Select>
          {ayarlar && !ayarlar.has_openai && !ayarlar.has_anthropic && !ayarlar.has_gemini && !ayarlar.has_custom && (
            <a href="/profile" className="text-[11px] text-amber-300 underline" data-testid="asistan-anahtar-yok">Anahtar yok — Profil › Yapay Zekâ</a>
          )}
          {/* İhale Bağlamı */}
          <Select value={selectedIhale} onValueChange={setSelectedIhale}>
            <SelectTrigger className="h-7 w-48 bg-gray-800 border-gray-700 text-white text-xs"><SelectValue placeholder="İhale Bağlamı" /></SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700">
              <SelectItem value="none" className="text-gray-300">Genel Soru</SelectItem>
              {(ihaleler || []).map(ih => <SelectItem key={ih.id} value={ih.id} className="text-gray-300 text-xs">{ih.is_adi}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" variant="ghost" className="h-7 text-gray-400 hover:text-white text-xs" onClick={() => setShowSessions(!showSessions)}>
            <Clock className="w-3.5 h-3.5 mr-1" /> Geçmiş
          </Button>
          <Button size="sm" variant="ghost" className="h-7 text-gray-400 hover:text-cyan-400 text-xs" onClick={newChat} data-testid="asistan-new-chat">
            <Plus className="w-3.5 h-3.5 mr-1" /> Yeni
          </Button>
          {selectedIhale !== 'none' && (
            <Button size="sm" onClick={handleProaktifAnaliz} disabled={proaktifLoading}
              className="h-7 bg-amber-600 hover:bg-amber-700 text-white text-xs" data-testid="asistan-proaktif">
              {proaktifLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1" />}
              Proaktif Analiz
            </Button>
          )}
        </div>
      </div>

      {/* Proaktif Analiz Sonucu */}
      {proaktifLoading && (
        <div className="mb-3 p-3 bg-amber-900/15 border border-amber-800/30 rounded-lg flex items-center gap-2">
          <Loader2 className="w-4 h-4 text-amber-400 animate-spin" />
          <span className="text-xs text-amber-300">Proaktif analiz yapılıyor... İhale verileri toplanıp AI tarafından analiz ediliyor.</span>
        </div>
      )}
      {proaktifResult && !proaktifLoading && (
        <div className="mb-3 bg-gray-900/80 border border-amber-800/20 rounded-lg overflow-hidden">
          <div className="px-3 py-2 bg-amber-900/20 border-b border-amber-800/20 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-400" />
              <span className="text-xs font-semibold text-amber-300">Proaktif Analiz Raporu</span>
            </div>
            <div className="flex items-center gap-3 text-[10px]">
              {proaktifResult.metrikleri && (
                <>
                  {proaktifResult.metrikleri.gecikme_gun > 0 && (
                    <span className="text-red-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {proaktifResult.metrikleri.gecikme_gun}g gecikme</span>
                  )}
                  {proaktifResult.metrikleri.acik_eksiklik > 0 && (
                    <span className="text-amber-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {proaktifResult.metrikleri.acik_eksiklik} açık eksiklik</span>
                  )}
                  {proaktifResult.metrikleri.uygunsuz_madde > 0 && (
                    <span className="text-orange-400 flex items-center gap-1"><Shield className="w-3 h-3" /> {proaktifResult.metrikleri.uygunsuz_madde} uygunsuz</span>
                  )}
                </>
              )}
              <button onClick={() => setProaktifResult(null)} className="text-gray-500 hover:text-gray-300">x</button>
            </div>
          </div>
          <div className="px-3 py-2 max-h-60 overflow-y-auto">
            <div className="text-xs text-gray-200 whitespace-pre-wrap leading-relaxed" data-testid="proaktif-sonuc">
              {proaktifResult.analiz}
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-1 gap-3 min-h-0">
        {/* Sessions Panel */}
        {showSessions && (
          <div className="w-56 flex-shrink-0 overflow-y-auto bg-gray-900/60 border border-gray-800 rounded-lg p-2 space-y-1">
            <p className="text-[10px] text-gray-500 font-semibold px-1">Son Sohbetler</p>
            {sessions.map(s => (
              <button key={s.session_id} onClick={() => loadSession(s.session_id)}
                className={`w-full text-left px-2 py-1.5 rounded text-xs transition-colors ${sessionId === s.session_id ? 'bg-cyan-900/30 text-cyan-300' : 'text-gray-400 hover:bg-gray-800'}`}>
                <MessageSquare className="w-3 h-3 inline mr-1" />
                <span className="truncate">{s.last_message?.substring(0, 40)}</span>
                <span className="text-[9px] text-gray-600 block">{s.count} mesaj</span>
              </button>
            ))}
            {sessions.length === 0 && <p className="text-[10px] text-gray-600 text-center py-4">Sohbet yok</p>}
          </div>
        )}

        {/* Chat Area */}
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto space-y-3 pb-3">
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-center px-4">
                <Bot className="w-12 h-12 text-cyan-400/30 mb-3" />
                <p className="text-sm text-gray-400 mb-1">Yapım İhaleleri Uzman Asistanı</p>
                <p className="text-xs text-gray-600 mb-3">4734/4735 sayılı kanunlar, YİGŞ, hakediş, kabul süreçleri ve kurumunuzun ihale verileri hakkında sorularınızı sorun.</p>
                {veriDurumu?.moduller && (
                  <div className="flex flex-wrap gap-1.5 justify-center max-w-xl mb-4" data-testid="asistan-veri-moduller">
                    {Object.entries(veriDurumu.moduller).filter(([, v]) => v > 0).map(([k, v]) => (
                      <span key={k} className="text-[10px] text-gray-400 bg-gray-900/70 border border-gray-800 rounded px-1.5 py-0.5">
                        <span className="text-cyan-300 font-semibold">{v}</span> {MODUL_ETIKET[k] || k}
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap gap-2 justify-center max-w-lg">
                  {HIZLI_SORULAR.map((q, i) => (
                    <button key={i} onClick={() => sendMessage(q)}
                      className="text-[11px] text-gray-400 bg-gray-800/60 hover:bg-gray-800 border border-gray-700/50 rounded-full px-3 py-1 transition-colors" data-testid={`quick-q-${i}`}>
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'ai' && <div className="w-6 h-6 rounded-full bg-cyan-900/40 flex items-center justify-center flex-shrink-0 mt-1"><Bot className="w-3.5 h-3.5 text-cyan-400" /></div>}
                <div className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${
                  msg.role === 'user' ? 'bg-[#1e3a5f] text-white' :
                  msg.error ? 'bg-red-900/30 text-red-300 border border-red-800/30' :
                  'bg-gray-800/80 text-gray-200'
                }`}>
                  {msg.role === 'ai' && !msg.error ? (
                    <div className="text-xs leading-relaxed [&_p]:my-1 [&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-4 [&_li]:my-0.5 [&_strong]:text-white [&_strong]:font-semibold [&_h1]:text-sm [&_h1]:font-bold [&_h1]:text-cyan-300 [&_h1]:mt-2 [&_h2]:text-sm [&_h2]:font-bold [&_h2]:text-cyan-300 [&_h2]:mt-2 [&_h3]:text-xs [&_h3]:font-bold [&_h3]:text-cyan-200 [&_h3]:mt-2 [&_table]:text-[11px] [&_table]:w-full [&_table]:my-1 [&_th]:border [&_th]:border-gray-700 [&_th]:px-1.5 [&_th]:py-0.5 [&_th]:bg-gray-900/60 [&_td]:border [&_td]:border-gray-700 [&_td]:px-1.5 [&_td]:py-0.5 [&_code]:bg-gray-900/70 [&_code]:px-1 [&_code]:rounded [&_hr]:border-gray-700 [&_hr]:my-2" data-testid="asistan-ai-mesaj">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                    </div>
                  ) : (
                    <div className="whitespace-pre-wrap text-xs leading-relaxed">{msg.content}</div>
                  )}
                  {msg.role === 'ai' && <KaynakSatiri kaynaklar={msg.kaynaklar} />}
                </div>
                {msg.role === 'user' && <div className="w-6 h-6 rounded-full bg-[#1e3a5f]/40 flex items-center justify-center flex-shrink-0 mt-1"><User className="w-3.5 h-3.5 text-blue-300" /></div>}
              </div>
            ))}
            {loading && (
              <div className="flex gap-2 items-center">
                <div className="w-6 h-6 rounded-full bg-cyan-900/40 flex items-center justify-center"><Bot className="w-3.5 h-3.5 text-cyan-400" /></div>
                <div className="bg-gray-800/80 rounded-lg px-3 py-2">
                  <Loader2 className="w-4 h-4 text-cyan-400 animate-spin" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="flex gap-2 pt-2 border-t border-gray-800">
            <Input value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown}
              placeholder="Sorunuzu yazın..." disabled={loading}
              className="bg-gray-800 border-gray-700 text-white text-sm h-9 flex-1" data-testid="asistan-input" />
            <Button onClick={() => sendMessage(input)} disabled={loading || !input.trim()} size="sm"
              className="bg-cyan-600 hover:bg-cyan-700 text-white h-9 px-3" data-testid="asistan-send">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
